import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LOGO from "../../../assets/img/MOSIDA-logo.png";
import FifaBg from "../../../assets/img/fifa/fifa-inner.png";
import FifaCardPreviewWithShimmer from "../../../compoment/features/FifaCardPreviewWithShimmer";
import HobbyResultConfirmModal from "../../../modal/HobbyResultConfirmModal";
import type { RootState } from "../../../redux/store";
import {
  isFifaWorldCup2026Station,
  persistFifaGeneratedCard,
  readActiveStoredStation,
  readFifaPlayerProfile,
  readFifaPlayerStats,
  resolveFifaCapturedImage,
  resolveFifaStationSlug,
  type FifaPlayerProfile,
  type FifaPlayerStats,
} from "../../../utils/stationFlowStorage";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";

const FifaCardCompare = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<{
    label: string;
    url: string;
  } | null>(null);
  const uploadedImageUrl = useSelector(
    (state: RootState) => state.uploadFlow.uploadedImageUrl
  );

  const stationData = useMemo(() => readActiveStoredStation(), [location.state]);
  const selectedAiSelfieArt = useMemo(
    () => readSelectedAiSelfieArtFromStorage(),
    [location.state]
  );
  const stationSlug = useMemo(
    () => resolveFifaStationSlug(stationData, selectedAiSelfieArt),
    [stationData, selectedAiSelfieArt]
  );
  const isFifaFlow = isFifaWorldCup2026Station(stationData, selectedAiSelfieArt);

  const locationState = location.state as {
    capturedImage?: string;
    stationData?: typeof stationData;
    fifaPlayerProfile?: FifaPlayerProfile;
    fifaPlayerStats?: FifaPlayerStats;
    cardOne?: string;
    cardTwo?: string;
    fifaStatsSource?: "manual" | "ai";
    fifaStatsSkipped?: boolean;
  } | null;

  const capturedImage = resolveFifaCapturedImage({
    locationImage: locationState?.capturedImage,
    reduxImage: uploadedImageUrl,
  });
  const fifaPlayerProfile =
    locationState?.fifaPlayerProfile || readFifaPlayerProfile();
  const fifaPlayerStats = locationState?.fifaPlayerStats || readFifaPlayerStats();
  const cardOne = locationState?.cardOne;
  const cardTwo = locationState?.cardTwo;

  const cards = useMemo(
    () =>
      [
        { label: "Result 1", url: cardOne },
        { label: "Result 2", url: cardTwo },
      ].filter((item) => typeof item.url === "string" && item.url.trim()),
    [cardOne, cardTwo]
  );

  const openConfirm = (label: string, url: string) => {
    setPendingSelection({ label, url });
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!pendingSelection?.url) return;

    const selectedCard = pendingSelection.url;
    setConfirmOpen(false);
    persistFifaGeneratedCard(selectedCard);
    navigate(`/${stationSlug}/save-fifa-card`, {
      state: {
        capturedImage,
        stationData,
        fifaPlayerProfile,
        fifaPlayerStats,
        fifaGeneratedCard: selectedCard,
        fifaStatsSource: locationState?.fifaStatsSource,
        fifaStatsSkipped: locationState?.fifaStatsSkipped,
        fifaCompareComplete: true,
      },
    });
  };

  useEffect(() => {
    if (!isFifaFlow) {
      navigate(`/${stationSlug}/choose-option`, { replace: true });
      return;
    }

    if (!capturedImage || !fifaPlayerProfile || cards.length < 2) {
      navigate(`/${stationSlug}/save-fifa-card`, { replace: true });
    }
  }, [capturedImage, cards.length, fifaPlayerProfile, isFifaFlow, navigate, stationSlug]);

  if (
    !isFifaFlow ||
    !capturedImage ||
    !fifaPlayerProfile ||
    cards.length < 2
  ) {
    return null;
  }

  return (
    <>
      <section
        className="fifaCardCompareSection"
        style={{ backgroundImage: `url(${FifaBg})` }}
      >
        <div className="fifaCardCompareInner">
          <div className="mosidaLogoWhite fifaCardCompareLogo mb-5">
            <img src={LOGO} alt="MOSIDA logo" className="logo" />
            <span className="badgeCreative">FIFA World Cup</span>
          </div>

          <div className="fifaCardCompareBody">
            <h1 className="fifaCardCompareHeading">
              <div className="fifaCardCompareHeadingLine mb-5">
                Select your{" "}
                <span className=" fifaCardCompareHeadingLine--gold">
                  preferred result
                </span>
              </div>
            </h1>

            <div className="fifaCardCompareGrid">
              {cards.map(({ label, url }) => (
                <div key={label} className="fifaCardCompareCard">
                  <div className="fifaCardCompareLabel">{label}</div>
                  <FifaCardPreviewWithShimmer
                    imageUrl={url}
                    alt={label}
                    variant="compare"
                  />
                  <button
                    type="button"
                    className="fifaCardCompareSelectBtn"
                    onClick={() => openConfirm(label, url!)}
                  >
                    Select
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {confirmOpen && pendingSelection && (
        <HobbyResultConfirmModal
          show={confirmOpen}
          onHide={() => setConfirmOpen(false)}
          selectionName={pendingSelection.label}
          imageUrl={pendingSelection.url}
          fallbackImage={pendingSelection.url}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default FifaCardCompare;
